import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { jsPDF } from "jspdf";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faReceipt,
  faIndianRupeeSign,
  faMoneyBillWave,
  faDownload,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

const SELLER_STATE = "Maharashtra"; // ← set to your registered business state
const GST_RATE = 0; // ← set actual combined GST % when known (e.g. 5, 12, 18)

const HerKartBill = () => {
  const { orderNo } = useParams();
  const navigate = useNavigate();
  const { fetchOrderByNo } = useHerKart();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setMessage(null);
      try {
        const response = await fetchOrderByNo(orderNo);
        if (response?.success && response.order) {
          setOrder(response.order);
        } else {
          setMessage({ type: "error", text: "Order not found." });
        }
      } catch (e) {
        console.error("Order fetch error:", e);
        setMessage({ type: "error", text: "Could not load this order." });
      } finally {
        setLoading(false);
      }
    };

    if (orderNo) load();
  }, [orderNo]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050816] text-white">
        <p className="text-slate-300">Loading invoice...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#050816] px-6 text-center text-white">
        <FontAwesomeIcon icon={faCircleExclamation} className="text-5xl text-red-400" />
        <h2 className="text-xl font-bold">{message?.text || "Order not found."}</h2>
        <button
          onClick={() => navigate("/herkart/orders")}
          className="mt-4 rounded-xl bg-pink-600 w-fit px-2 py-1 font-semibold hover:bg-pink-500"
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const isIntraState =
    (order.customer?.state || "").trim().toLowerCase() === SELLER_STATE.toLowerCase();

  const subtotal = order.subtotal ?? order.items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const delivery = order.delivery ?? 0;

  const taxableAmount = subtotal;
  const gstAmount = Math.round((taxableAmount * GST_RATE) / 100);
  const cgst = isIntraState ? Math.round(gstAmount / 2) : 0;
  const sgst = isIntraState ? Math.round(gstAmount / 2) : 0;
  const igst = !isIntraState ? gstAmount : 0;

  const grandTotal = order.grandTotal ?? subtotal + delivery + gstAmount;

  const invoiceNo = `INV-${order.orderNo}`;
  const invoiceDate = new Date(order.orderedAt).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

    const downloadBill = () => {
    const pdf = new jsPDF();

    // Header
    pdf.setFontSize(18);
    pdf.text("Think5Whys FoundHer Marketplace", 20, 20);

    pdf.setFontSize(14);
    pdf.text("HerKart Invoice", 20, 30);

    pdf.setFontSize(11);
    pdf.text(`Invoice No : ${invoiceNo}`, 20, 45);
    pdf.text(`Order No : ${order.orderNo}`, 20, 53);
    pdf.text(`Date : ${invoiceDate}`, 20, 61);
    pdf.text(`Payment : ${order.paymentMode || "Cash On Delivery"}`, 20, 69);

    // Customer
    pdf.text("Customer", 20, 85);
    pdf.text(order.customer?.name || "", 20, 93);
    pdf.text(order.customer?.mobile || "", 20, 101);
    pdf.text(`${order.customer?.house || ""}, ${order.customer?.street || ""}`, 20, 109);
    pdf.text(`${order.customer?.city || ""}, ${order.customer?.state || ""} - ${order.customer?.pincode || ""}`, 20, 117);

    let y = 135;

    // Items
    order.items.forEach((item) => {
      const line = `${item.name} x${item.quantity} Rs.${item.price} Rs.${item.price * item.quantity}`;
      pdf.text(line, 20, y);
      y += 8;
    });

    y += 10;

    pdf.text(`Subtotal : Rs.${subtotal}`, 20, y);
    y += 8;
    pdf.text(`Delivery : Rs.${delivery}`, 20, y);
    y += 8;

    if (isIntraState) {
      pdf.text(`CGST (${(GST_RATE / 2).toFixed(1)}%) : Rs.${cgst}`, 20, y);
      y += 8;
      pdf.text(`SGST (${(GST_RATE / 2).toFixed(1)}%) : Rs.${sgst}`, 20, y);
      y += 8;
    } else {
      pdf.text(`IGST (${GST_RATE}%) : Rs.${igst}`, 20, y);
      y += 8;
    }

    y += 6;
    pdf.setFontSize(14);
    pdf.text(`Grand Total : Rs.${grandTotal}`, 20, y);

    y += 20;
    pdf.setFontSize(10);
    pdf.text("Thank you for supporting a local woman entrepreneur.", 20, y);

    pdf.save(`${invoiceNo}.pdf`);
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="mx-auto max-w-3xl p-5">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-pink-400">
                <FontAwesomeIcon icon={faReceipt} className="mr-2" />
                HerKart Invoice
              </h1>

              <p className="text-sm text-slate-100">Think5Whys FoundHer Marketplace</p>
            </div>

            <button
              onClick={downloadBill}
              className="rounded-xl bg-pink-600 w-fit px-2 py-1 hover:bg-pink-500"
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
          </div>

          <div className="mt-6 grid gap-2 text-sm md:grid-cols-2">
            <p className="text-gray-100"><b>Invoice :</b> {invoiceNo}</p>
            <p className="text-gray-100"><b>Order :</b> {order.orderNo}</p>
            <p className="text-gray-100"><b>Date :</b> {invoiceDate}</p>
            <p className="text-gray-100">
              <b>Payment :</b>{" "}
              <FontAwesomeIcon icon={faMoneyBillWave} className="mr-1 text-green-400" />
              {order.paymentMode || "Cash On Delivery"}
            </p>
          </div>

          <hr className="my-6 border-white/10" />

          <h3 className="mb-2 font-semibold">Customer</h3>
          <p className="text-gray-100">{order.customer?.name}</p>
          <p className="text-gray-100">{order.customer?.mobile}</p>
          <p className="text-gray-100">{order.customer?.house}, {order.customer?.street}</p>
          <p className="text-gray-100">
            {order.customer?.city}, {order.customer?.state} - {order.customer?.pincode}
          </p>

          <hr className="my-6 border-white/10" />

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="py-2">Product</th>
                <th>Qty</th>
                <th>Rate</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>

            <tbody>
              {order.items.map((item, index) => (
                <tr key={item.productId || index} className="border-b border-white/5">
                  <td className="py-3">{item.name}</td>
                  <td>{item.quantity}</td>
                  <td>₹{item.price}</td>
                  <td className="text-right">₹{item.price * item.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 space-y-2 text-right">
            <p className="text-gray-100">Subtotal : ₹{subtotal}</p>
            <p className="text-gray-100">Delivery : ₹{delivery}</p>

            {isIntraState ? (
              <>
                <p className="text-gray-100">CGST ({(GST_RATE / 2).toFixed(1)}%) : ₹{cgst}</p>
                <p className="text-gray-100">SGST ({(GST_RATE / 2).toFixed(1)}%) : ₹{sgst}</p>
              </>
            ) : (
              <p className="text-gray-100">IGST ({GST_RATE}%) : ₹{igst}</p>
            )}

            <h2 className="pt-3 text-2xl font-bold text-pink-400">
              <FontAwesomeIcon icon={faIndianRupeeSign} className="mr-1" />
              {grandTotal}
            </h2>
          </div>

          <div className="mt-8 rounded-2xl bg-pink-500/10 p-4 text-center text-sm text-slate-100">
            💜 Thank you for supporting a local woman entrepreneur.
            <br />
            This is a computer generated invoice.
          </div>

          <div className="mt-8 flex gap-3">
            <Link
              to="/herkart/orders"
              className="flex-1 rounded-xl border border-white/10 py-3 text-center hover:bg-white/10"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back to Orders
            </Link>

            <button
              onClick={downloadBill}
              className="flex-1 rounded-xl bg-pink-600 w-fit px-2 py-1 font-semibold hover:bg-pink-500"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Download Bill
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HerKartBill;